export interface CostDish {
  id: string
  name: string
  region?: string
  ingredients?: { id: string; name: string }[]
}

export interface PricedIngredient {
  id: string
  name: string
  price: number
  unitOfMeasure?: string
  stockQuantity?: number | null
}

export interface IngredientCostRow {
  ingredientId: string
  ingredientName: string
  unitPrice: number
  unitOfMeasure: string
  stockQuantity: number | null
  dishUsage: { dishId: string; dishName: string; estimatedCostShare: number }[]
  estimatedTotalInRecipes: number
}

export interface DishMarginRow {
  dishId: string
  dishName: string
  foodCost: number
  menuPrice: number | null
  margin: number | null
  marginPct: number | null
}

export function parsePrice(v?: string | number | null): number {
  if (v == null) return 0
  const n = typeof v === 'string' ? parseFloat(v) : v
  return Number.isFinite(n) ? n : 0
}

export function buildPriceMap(ingredients: PricedIngredient[]): Map<string, number> {
  return new Map(ingredients.map((i) => [i.id, i.price]))
}

/** Sum catalog unit prices for each ingredient in a dish (1 unit each when recipe qty is unknown). */
export function estimateDishFoodCost(dish: CostDish, priceById: Map<string, number>): number {
  return (dish.ingredients ?? []).reduce((sum, ing) => sum + (priceById.get(ing.id) ?? 0), 0)
}

export function buildIngredientCostRows(
  dishes: CostDish[],
  priced: PricedIngredient[],
): IngredientCostRow[] {
  const priceById = buildPriceMap(priced)
  return priced.map((ing) => {
    const dishUsage = dishes
      .filter((d) => d.ingredients?.some((x) => x.id === ing.id))
      .map((d) => {
        const count = d.ingredients?.length ?? 1
        const unit = priceById.get(ing.id) ?? 0
        return {
          dishId: d.id,
          dishName: d.name,
          estimatedCostShare: Math.round((unit / count) * 100) / 100,
        }
      })
    const estimatedTotalInRecipes = dishUsage.reduce((s, u) => s + u.estimatedCostShare, 0)
    return {
      ingredientId: ing.id,
      ingredientName: ing.name,
      unitPrice: ing.price,
      unitOfMeasure: ing.unitOfMeasure ?? 'unit',
      stockQuantity: ing.stockQuantity ?? null,
      dishUsage,
      estimatedTotalInRecipes: Math.round(estimatedTotalInRecipes * 100) / 100,
    }
  })
}

export function buildDishMarginRows(
  dishes: CostDish[],
  priceById: Map<string, number>,
  menuPricesByDishName: Map<string, number>,
): DishMarginRow[] {
  return dishes.map((d) => {
    const foodCost = Math.round(estimateDishFoodCost(d, priceById) * 100) / 100
    const menuPrice = menuPricesByDishName.get(d.name.toLowerCase()) ?? null
    const margin = menuPrice != null ? Math.round((menuPrice - foodCost) * 100) / 100 : null
    const marginPct =
      menuPrice != null && menuPrice > 0
        ? Math.round(((menuPrice - foodCost) / menuPrice) * 1000) / 10
        : null
    return { dishId: d.id, dishName: d.name, foodCost, menuPrice, margin, marginPct }
  })
}

export function mergePricedIngredients(
  catalog: { id: string; name: string; price?: string; unitOfMeasure?: string }[],
  mine: { id: string; name: string; price?: string; unitOfMeasure?: string; catalogIngredientId?: string; stockQuantity?: number | null }[],
): PricedIngredient[] {
  const map = new Map<string, PricedIngredient>()
  for (const c of catalog) {
    map.set(c.id, {
      id: c.id,
      name: c.name,
      price: parsePrice(c.price),
      unitOfMeasure: c.unitOfMeasure,
    })
  }
  for (const m of mine) {
    const key = m.catalogIngredientId ?? m.id
    const existing = map.get(key)
    map.set(key, {
      id: key,
      name: m.name,
      price: parsePrice(m.price) || existing?.price || 0,
      unitOfMeasure: m.unitOfMeasure ?? existing?.unitOfMeasure,
      stockQuantity: m.stockQuantity ?? null,
    })
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
}
